import * as React from 'react';
import {useSelector} from '@xstate/react';
import {zodResolver} from '@hookform/resolvers/zod';
import {FormProvider, useForm} from 'react-hook-form';
import useLabelTranslation from 'src/hooks/useLabelTranslation';

import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {InContextAlert} from '@spaced-out/ui-design-system/lib/components/InContextAlert';

import {
  selectBookingFormInputs,
  selectAdditionalQuestions,
  selectShouldShowRequestDetailsStep,
} from '../../selectors/booking-selector';
import {getDefaultFormValues} from '../../utils/form';
import {useRootMachine} from '../../hooks/useRootMachine';
import {selectSession} from '../../selectors/session-selector';
import {BrandButton} from '../../lib/brand-button/brand-button';
import {useBookingMachine} from '../../hooks/useBookingMachine';
import {RequestDetailsFields} from './request-more-availability-details-fields';
import {ConfirmProceedModal} from './confirm-proceed-modal';
import {RequesterInformationFields} from './request-more-availability-requester-fields';
import {selectCountryCode, selectError} from '../../selectors/commonSelector';
import {getRequestMoreAvailabilitySchema} from './request-more-availability.schema';
import type {RequestMoreAvailabilityFormValues} from './request-more-availability.types';
import {
  getAvailableTimeSlotsByDate,
  parseSlotRequestLimitsFromRuleValue,
} from './request-more-availability.utils';
import {formatIsoDate} from 'src/utils/date-time';

import css from './request-more-availability-form.css';


export const RequestMoreAvailabilityForm = () => {
  const {t, language} = useLabelTranslation();

  // machines
  const {rootActor} = useRootMachine();
  const {bookingActor, bookingState} = useBookingMachine();

  // selectors
  const {errorMessage, allowRetry} = useSelector(bookingActor, (state) =>
    selectError(state, t),
  );
  const session = useSelector(rootActor, (state) => selectSession(state));
  const baseFormInputs = useSelector(bookingActor, (state) =>
    selectBookingFormInputs(state, t),
  );
  const countyCode = useSelector(rootActor, (state) =>
    selectCountryCode(state),
  );
  const additionalQuestions = useSelector(bookingActor, (state) =>
    selectAdditionalQuestions(state),
  );
  const shouldShowRequestDetailsStep = useSelector(
    bookingActor,
    selectShouldShowRequestDetailsStep,
  );

  const minDate = formatIsoDate(new Date());

  // Allow up to 14 days beyond the template's extended (or base) end date.
  const maxDate = useSelector(bookingActor, (state) => {
    const range = state.context.availabilityRange;
    const endTs = range?.extended_end_at ?? range?.end_at;
    if (!endTs) {
      return undefined;
    }
    return formatIsoDate(new Date((endTs + 14 * 86400) * 1000));
  });

  const slotRequestLimits = useSelector(bookingActor, (state) => {
    const slotRequestRule = state.context.eventTemplate?.rules?.find(
      (rule) => rule.rule_type === 'slot_request_rule',
    );
    return parseSlotRequestLimitsFromRuleValue(slotRequestRule?.value);
  });

  const sequenceDays = useSelector(
    bookingActor,
    (state) => state.context.sequenceDays,
  );
  const availableTimeSlots = React.useMemo(
    () => getAvailableTimeSlotsByDate(sequenceDays),
    [sequenceDays],
  );

  // state
  const isRequestDetailsStep = bookingState.matches({
    requestMoreAvailabilityForm: 'requestDetails',
  });
  const isRequesterInformationStep = bookingState.matches({
    requestMoreAvailabilityForm: 'requesterInformation',
  });
  const isSubmitting = bookingState.matches({
    requestMoreAvailabilityForm: 'submittingSlotRequest',
  });
  const hasError = bookingState.matches({
    requestMoreAvailabilityForm: 'retrySubmittingSlotRequest',
  });

  const [isConfirmModalOpen, setIsConfirmModalOpen] = React.useState(false);

  // form schema
  const requestMoreAvailabilityFormSchema = React.useMemo(
    () =>
      getRequestMoreAvailabilitySchema({
        t,
        items: baseFormInputs,
        countyCode,
        includeRequestDetailsStep: shouldShowRequestDetailsStep,
        slotRequestLimits,
        availableTimeSlots,
      }),
    [
      countyCode,
      baseFormInputs,
      shouldShowRequestDetailsStep,
      slotRequestLimits,
      availableTimeSlots,
      t,
    ],
  );

  const {defaultValues, disabledFields} = getDefaultFormValues(
    baseFormInputs,
    session,
  );

  const methods = useForm({
    defaultValues: {
      requestReason: '',
      requestedSlots: [{date: '', start_time: '', end_time: ''}],
      ...defaultValues,
    },
    resolver: zodResolver(requestMoreAvailabilityFormSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const onSubmit = () => {
    const formValues =
      methods.getValues() as RequestMoreAvailabilityFormValues &
        Record<string, unknown>;
    const {requestReason, requestedSlots, guestName, guestEmail, guestPhone} =
      formValues;

    const updatedAdditionalQuestionAnswers = additionalQuestions.map(
      (question, index) => ({
        question: question.question,
        answer: (formValues[`additionalQuestion-${index}`] as string) || '',
      }),
    );

    bookingActor.send({
      type: 'requestMoreAvailability.requesterInfo.submit',
      ...(shouldShowRequestDetailsStep && {
        requestReason: requestReason ?? '',
        requestedSlots: requestedSlots ?? [],
      }),
      guestName: guestName as string,
      guestEmail: guestEmail as string,
      guestPhone: guestPhone as string,
      additionalQuestionAnswers: updatedAdditionalQuestionAnswers,
    });
  };

  const handleStepOneNext = async () => {
    // trigger() with no args applies ALL errors, triggers all validation on click of next
    await methods.trigger();
    const {errors} = methods.formState;
    if (errors.requestReason || errors.requestedSlots) {
      return;
    }
    // Clear errors before moving to step 2 so step-2 fields start clean.
    methods.clearErrors();
    bookingActor.send({
      type: 'requestMoreAvailability.requestDetails.next',
    });
  };

  const handleFormSubmit = methods.handleSubmit(() => {
    if (session?.id) {
      setIsConfirmModalOpen(true);
    } else {
      onSubmit();
    }
  });

  const handleConfirmSubmit = () => {
    setIsConfirmModalOpen(false);
    methods.handleSubmit(onSubmit)();
  };

  const handleBack = () => {
    if (isRequesterInformationStep && shouldShowRequestDetailsStep) {
      bookingActor.send({type: 'requestMoreAvailability.requesterInfo.back'});
      return;
    }
    bookingActor.send({type: 'requestMoreAvailabilityForm.back'});
  };

  React.useEffect(() => {
    methods.reset();
  }, [language, methods]);

  return (
    <>
      <FormProvider {...methods}>
        <form
          className={css.formContainer}
          onSubmit={isRequesterInformationStep ? handleFormSubmit : undefined}
        >
          <div className={css.formContent}>
            <div className={css.questionContainer}>
              {isRequestDetailsStep && (
                <RequestDetailsFields
                  slotRequestLimits={slotRequestLimits}
                  minDate={minDate}
                  maxDate={maxDate}
                />
              )}
              {(isRequesterInformationStep || isSubmitting) && (
                <RequesterInformationFields
                  formInputs={baseFormInputs}
                  disabledFields={disabledFields}
                />
              )}
            </div>
          </div>
          <div className={css.submitButtonSection}>
            <div className={css.buttonContainer}>
              <Button
                type="tertiary"
                size="medium"
                iconLeftName="chevron-left"
                onClick={handleBack}
                disabled={isSubmitting}
              >
                {t('Back', 'Back')}
              </Button>
              {isRequestDetailsStep && (
                <BrandButton
                  actionType="button"
                  size="medium"
                  onClick={handleStepOneNext}
                  disabled={isSubmitting}
                >
                  {t('Next', 'Next')}
                </BrandButton>
              )}
              {isRequesterInformationStep && (
                <BrandButton
                  actionType="submit"
                  size="medium"
                  disabled={hasError && !allowRetry}
                  isLoading={isSubmitting}
                >
                  {t('Submit', 'Submit')}
                </BrandButton>
              )}
            </div>
            {hasError && (
              <InContextAlert
                leftIconName="shield-exclamation"
                semantic="danger"
              >
                {/* already translated in errorMessage */}
                {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
                {errorMessage}
              </InContextAlert>
            )}
          </div>
        </form>
      </FormProvider>
      <ConfirmProceedModal
        isOpen={isConfirmModalOpen}
        hasError={hasError}
        isSubmitting={isSubmitting}
        onConfirm={handleConfirmSubmit}
        onClose={() => setIsConfirmModalOpen(false)}
      />
    </>
  );
};
